import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  OutlinedInput,
  Slider,
  Typography,
} from "@mui/material";
import { ProviderFile } from "fileParsing/types";
import { TypeSchema, UnionType, WranglerItem } from "fileParsing/wrangler/types";
import React, { useState } from "react";
import { WranglerProcessor } from "webworker/Wrangler/WranglerProcessor";

export class FileFilter {
  entitiesWithoutTimestamp = false;
  entitiesWithoutCategory = false;
  showSingleFiles = false;
  showCollections = false;

  rangeSize: [number, number] = [0, Number.MAX_SAFE_INTEGER];
  rangeDataPoints: [number, number] = [0, Number.MAX_SAFE_INTEGER];

  freetext = "";

  apply(lst: WranglerItem[], files: ProviderFile[]): WranglerItem[] {
    const filterSingleFiles = (item: WranglerItem) => this.showSingleFiles && item.files.length <= 1;
    const filterCollections = (item: WranglerItem) => this.showCollections && item.files.length > 1;

    const filterTimestamp = () => this.entitiesWithoutTimestamp && true;
    const filterCategory = () => this.entitiesWithoutCategory && true;

    const filterRanges = (item: WranglerItem) => {
      const itemFiles = WranglerProcessor.matchFiles(item, files);
      const { size, dp } = itemFiles.reduce(
        (dim, file) => ({
          size: dim.size + file.uncompressedSize,
          dp: dim.dp + file.data.length,
        }),
        { size: 0, dp: 0 },
      );

      return (
        this.rangeSize[0] <= size &&
        this.rangeSize[1] >= size &&
        this.rangeDataPoints[0] <= dp &&
        this.rangeDataPoints[1] >= dp
      );
    };

    const freeTextToken = this.freetext.toLowerCase().split(/\W/g);
    const filterFreeText = (item: WranglerItem) => {
      if (freeTextToken.length === 0) return true;
      // Search in path
      if (freeTextToken.some((token) => item.files.some((file) => file.path.toLowerCase().includes(token))))
        return true;

      // Search in type attributes
      // #region Search Attributes in Type Schema
      const typeDescAttributes = (schema: TypeSchema): string[] => {
        if (schema.type === "array") {
          return schema.anyOf.reduce<string[]>((flat, desc) => [...flat, ...typeDescAttributes(desc)], []);
        } else if (schema.type === "object") {
          return Object.entries(schema.properties).reduce<string[]>(
            (flat, [k, v]) => [...flat, k, ...unionDescAttributes(v)],
            [],
          );
        }

        return [];
      };
      const unionDescAttributes = (schema: UnionType): string[] =>
        schema.reduce<string[]>((flat, desc) => [...flat, ...typeDescAttributes(desc)], []);

      // #endregion
      const attributes = (item.schema ? unionDescAttributes(item.schema) : []).join(" ").toLowerCase();
      if (attributes.length > 0 && freeTextToken.some((token) => attributes.includes(token))) return true;

      // Search in templates (text)
      // Extract all text values from templates
      const templateText = item.templates
        .map(
          (template) =>
            // Extract text from subcategory
            template.subcategory
              .filter((dataItem) => dataItem.type === "text")
              .map((dataItem) => dataItem.value)
              .join(" ") +
            " " +
            // Extract text from content
            template.content
              .filter((dataItem) => dataItem.type === "text")
              .map((dataItem) => dataItem.value)
              .join(" "),
        )
        // join and lowercase text of all template-text found
        .join(" ")
        .toLowerCase();
      // Try to find text from templates
      if (templateText.length > 0 && freeTextToken.some((token) => templateText.includes(token))) return true;

      return false;
    };

    return lst.filter((item) => {
      return (
        // Filter by type of TreeItem -> Single File/Collection
        (this.showSingleFiles === this.showCollections || filterSingleFiles(item) || filterCollections(item)) &&
        // Filter by missing details
        ((this.entitiesWithoutTimestamp === false && this.entitiesWithoutCategory === false) ||
          filterTimestamp() ||
          filterCategory()) &&
        // Filter by ranges
        filterRanges(item) &&
        // Free text search
        filterFreeText(item)
      );
    });
  }

  copyWith(newVals: Partial<FileFilter>): FileFilter {
    return Object.assign(Object.create(FileFilter.prototype), this, newVals);
  }
}

type FileFilterPanelProps = {
  filters: FileFilter;
  onChange: (filter: FileFilter) => void;
  rangeSize: [number, number];
  rangeDataPoints: [number, number];
};

export const FileFilterPanel: React.FC<FileFilterPanelProps> = ({ filters, onChange, rangeSize, rangeDataPoints }) => {
  const [openFilter, setOpenFilter] = useState<number>();

  return (
    <Box>
      <Box ml={"20px"}>
        <Typography variant="caption">Filter:</Typography>
      </Box>
      <Grid container alignItems="stretch">
        <Grid item xs={3}>
          <Button
            sx={{ height: "100%", width: "100%" }}
            variant={openFilter === 0 ? "contained" : "text"}
            onClick={() => setOpenFilter(openFilter === 0 ? undefined : 0)}
          >
            Missing Details
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            sx={{ height: "100%", width: "100%" }}
            variant={openFilter === 1 ? "contained" : "text"}
            onClick={() => setOpenFilter(openFilter === 1 ? undefined : 1)}
          >
            Type
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            sx={{ height: "100%", width: "100%" }}
            variant={openFilter === 2 ? "contained" : "text"}
            onClick={() => setOpenFilter(openFilter === 2 ? undefined : 2)}
          >
            Size
          </Button>
        </Grid>
        <Grid item xs={3}>
          <Button
            sx={{ height: "100%", width: "100%" }}
            variant={openFilter === 3 ? "contained" : "text"}
            onClick={() => setOpenFilter(openFilter === 3 ? undefined : 3)}
          >
            Data Points
          </Button>
        </Grid>
      </Grid>
      <Box sx={{ background: "#202020" }}>
        <Collapse in={openFilter === 0}>
          {/* Select Field: Filter by missing details */}
          <FormControl fullWidth component="fieldset" variant="standard" sx={{ padding: [0, 2] }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.entitiesWithoutTimestamp}
                  onChange={(ev) =>
                    onChange(
                      filters.copyWith({
                        entitiesWithoutTimestamp: ev.target.checked,
                      }),
                    )
                  }
                />
              }
              label="Show Entities without Timestamp"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.entitiesWithoutCategory}
                  onChange={(ev) =>
                    onChange(
                      filters.copyWith({
                        entitiesWithoutCategory: ev.target.checked,
                      }),
                    )
                  }
                />
              }
              label="Show Entities without Category"
            />
          </FormControl>
        </Collapse>

        <Collapse in={openFilter === 1}>
          {/* Select Field: Filter by Single File/Collection of similar Files */}
          <FormControl fullWidth component="fieldset" variant="standard" sx={{ padding: [0, 2] }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showSingleFiles}
                  onChange={(ev) => onChange(filters.copyWith({ showSingleFiles: ev.target.checked }))}
                />
              }
              label="Single Files"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.showCollections}
                  onChange={(ev) => onChange(filters.copyWith({ showCollections: ev.target.checked }))}
                />
              }
              label="Collection of similar Files"
            />
          </FormControl>
        </Collapse>

        <Collapse in={openFilter === 2}>
          {/* Filter by File Size */}
          <SliderFilter
            title="By File Size"
            unit="B"
            range={rangeSize}
            value={filters.rangeSize}
            onChange={(rangeSize) => onChange(filters.copyWith({ rangeSize }))}
          />
        </Collapse>

        <Collapse in={openFilter === 3}>
          {/* Filter by DataPoint Count */}
          <SliderFilter
            title="By possible Data Points"
            range={rangeDataPoints}
            value={filters.rangeDataPoints}
            onChange={(rangeDataPoints) => onChange(filters.copyWith({ rangeDataPoints }))}
          />
        </Collapse>
      </Box>
    </Box>
  );
};

type SliderFilterProps = {
  title: string;
  unit?: string;
  range: [number, number];
  value: [number, number];
  onChange: (range: [number, number]) => void;
};

const SliderFilter: React.FC<SliderFilterProps> = ({ title, unit, range, value, onChange }) => {
  return (
    <Box
      sx={{
        padding: [0, 2],
      }}
    >
      <Typography>{title}</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <OutlinedInput
          label="Min"
          sx={{ width: 120 }}
          type="number"
          size="small"
          value={Math.max(value[0], range[0])}
          onChange={(ev) => onChange([parseInt(ev.target.value), value[1]])}
          endAdornment={unit && <InputAdornment position="end">{unit}</InputAdornment>}
        />
        <OutlinedInput
          label="Max"
          sx={{ width: 120 }}
          type="number"
          size="small"
          value={Math.min(value[1], range[1])}
          onChange={(ev) => onChange([value[0], parseInt(ev.target.value)])}
          endAdornment={unit && <InputAdornment position="end">{unit}</InputAdornment>}
        />
      </Box>
      <Slider
        min={range[0]}
        max={range[1]}
        value={value}
        onChange={(_, value) => onChange(value as [number, number])}
      />
    </Box>
  );
};
